/**
 * Cliente da API da Criação do Mundo.
 *
 * <p>As dez coleções compartilham o mesmo formato de rota, então quase tudo aqui é
 * genérico: o que muda entre uma criatura e uma facção é só o pedaço da URL. É isso
 * que permite as telas serem uma só, dirigidas pelo esquema.
 */
import { api } from '../../api/cliente';
import type { ConteudoInGame } from './divisaDaFicha';
import type {
  ArquivoSalvo,
  CategoriaDeItem,
  Colecao,
  Ficha,
  Mapa,
  PaginaDaPericia,
  Progresso,
  Resumo,
  Vinculos,
} from './tipos';

const PROJETO = 'mistik-island';
const BASE = `/api/projetos/${PROJETO}/mundo`;

export function listar(colecao: Colecao): Promise<Resumo[]> {
  return api<Resumo[]>(`${BASE}/${colecao}`);
}

export function carregar<T extends Ficha>(colecao: Colecao, id: number): Promise<T> {
  return api<T>(`${BASE}/${colecao}/${id}`);
}

/** Criar pede só o nome — o resto se preenche depois, e é por isso que a ficha nasce como template. */
export function criar<T extends Ficha>(
  colecao: Colecao,
  corpo: Record<string, unknown>
): Promise<T> {
  return api<T>(`${BASE}/${colecao}`, { method: 'POST', body: JSON.stringify(corpo) });
}

export function salvar<T extends Ficha>(
  colecao: Colecao,
  id: number,
  mudancas: Record<string, unknown>
): Promise<T> {
  return api<T>(`${BASE}/${colecao}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(mudancas),
  });
}

export function vinculos(colecao: Colecao, id: number): Promise<Vinculos> {
  return api<Vinculos>(`${BASE}/${colecao}/${id}/vinculos`);
}

/**
 * Exclui. Sem {@code confirmado}, a API responde 409 quando há vínculos — é assim que
 * a tela sabe que precisa perguntar antes, e o que ela mostra vem de {@link vinculos}.
 */
export function excluir(colecao: Colecao, id: number, confirmado = false): Promise<void> {
  return api<void>(`${BASE}/${colecao}/${id}?confirmar=${confirmado}`, { method: 'DELETE' });
}

// ------------------------------------------------- relações pelo outro lado

/** Vincula um local existente a uma área. Escreve em `local.area_id`. */
export function vincularLocalNaArea(areaId: number, localId: number): Promise<unknown> {
  return api(`${BASE}/areas/${areaId}/locais`, {
    method: 'POST',
    body: JSON.stringify({ localId }),
  });
}

export function desvincularLocalDaArea(areaId: number, localId: number): Promise<unknown> {
  return api(`${BASE}/areas/${areaId}/locais/${localId}`, { method: 'DELETE' });
}

/** Coloca um NPC existente num local. Escreve em `npc.local_id`. */
export function vincularNpcNoLocal(localId: number, npcId: number): Promise<unknown> {
  return api(`${BASE}/locais/${localId}/npcs`, {
    method: 'POST',
    body: JSON.stringify({ npcId }),
  });
}

export function desvincularNpcDoLocal(localId: number, npcId: number): Promise<unknown> {
  return api(`${BASE}/locais/${localId}/npcs/${npcId}`, { method: 'DELETE' });
}

// ------------------------------------------------------- relações de raça

export function salvarRelacaoDeRaca(
  racaId: number,
  alvoId: number,
  texto: string
): Promise<unknown> {
  return api(`${BASE}/racas/${racaId}/relacoes`, {
    method: 'PUT',
    body: JSON.stringify({ alvoId, texto }),
  });
}

export function excluirRelacaoDeRaca(racaId: number, alvoId: number): Promise<unknown> {
  return api(`${BASE}/racas/${racaId}/relacoes/${alvoId}`, { method: 'DELETE' });
}

// --------------------------------------------------------------- receita

/** Põe um ingrediente na receita de um item, ou muda a quantidade dele. */
export function salvarIngrediente(
  itemId: number,
  ingredienteId: number,
  quantidade: number
): Promise<unknown> {
  return api(`${BASE}/itens/${itemId}/receita`, {
    method: 'PUT',
    body: JSON.stringify({ ingredienteId, quantidade }),
  });
}

export function excluirIngrediente(itemId: number, ingredienteId: number): Promise<unknown> {
  return api(`${BASE}/itens/${itemId}/receita/${ingredienteId}`, { method: 'DELETE' });
}

// --------------------------------------------------------------- perícias

/** A página de uma perícia — só leitura, ver `PericiaService.java`. */
export function carregarPericia(pericia: string): Promise<PaginaDaPericia> {
  return api<PaginaDaPericia>(`${BASE}/pericias/${pericia}`);
}

// ------------------------------------------------------------------- mapa

export function carregarMapa(): Promise<Mapa> {
  return api<Mapa>(`${BASE}/mapa`);
}

/** Quantas fichas há em cada coleção — os números ao lado do menu. */
export function contagem(): Promise<Record<string, number>> {
  return api<Record<string, number>>(`${BASE}/contagem`);
}

/**
 * O que, em cada coleção, é conteúdo do jogador — a régua da caixa dourada.
 *
 * <p>Quem é dono disto é o backend (`ConteudoInGame.java`), porque é dele que a
 * exportação para a engine vai sair um dia. A tela só consome; ver `divisaDaFicha`.
 *
 * <p>É metadado: não depende do banco e não muda entre uma visita e outra. Quem chama
 * guarda com `staleTime: Infinity`, como as perícias.
 */
export function carregarInGame(): Promise<ConteudoInGame> {
  return api<ConteudoInGame>(`${BASE}/in-game`);
}

export function moverRegiao(id: number, indiceMapa: number): Promise<unknown> {
  return api(`${BASE}/regioes/${id}/posicao`, {
    method: 'PATCH',
    body: JSON.stringify({ indiceMapa }),
  });
}

export function moverArea(
  id: number,
  indiceMapa: number,
  regiaoId?: number
): Promise<unknown> {
  return api(`${BASE}/areas/${id}/posicao`, {
    method: 'PATCH',
    body: JSON.stringify({ indiceMapa, regiaoId }),
  });
}

// ------------------------------------------------------------- progresso

export function carregarProgresso(): Promise<Progresso> {
  return api<Progresso>(`${BASE}/progresso`);
}

/**
 * Muda a meta de uma coleção e recebe o painel inteiro recalculado.
 *
 * A chave vai crua no caminho, inclusive o dois-pontos de `itens:craft`: ele é
 * caractere válido num segmento de URL, e escapá-lo faria o pedido passar pelo
 * filtro de segurança do Spring com um `%3A` que só atrapalha ao ler o log.
 */
export function salvarMeta(colecao: string, porRegiao: number): Promise<Progresso> {
  return api<Progresso>(`${BASE}/progresso/metas/${colecao}`, {
    method: 'PATCH',
    body: JSON.stringify({ porRegiao }),
  });
}

// ------------------------------------------- descrição das categorias de item

/**
 * As descrições que o time escreveu para as categorias de item.
 *
 * <p>Só vêm as que têm texto. Categoria sem linha é categoria sem descrição — a tela
 * mostra o convite a escrever, e não uma frase vazia que pareceria defeito.
 */
export function carregarCategoriasDeItem(): Promise<CategoriaDeItem[]> {
  return api<CategoriaDeItem[]>(`${BASE}/categorias-de-item`);
}

/**
 * Reescreve a descrição de uma categoria e recebe a lista inteira de volta.
 *
 * <p>Devolver tudo, e não só a linha editada, pela mesma razão do painel de progresso:
 * é o que a tela precisa para se redesenhar sem uma segunda chamada. Texto em branco
 * apaga a descrição — é uma escolha legítima, porque frase errada é pior que nenhuma.
 */
export function salvarDescricaoDaCategoria(
  categoria: string,
  descricao: string
): Promise<CategoriaDeItem[]> {
  return api<CategoriaDeItem[]>(`${BASE}/categorias-de-item/${categoria}`, {
    method: 'PATCH',
    body: JSON.stringify({ descricao }),
  });
}

// --------------------------------------------------------------- imagens

/**
 * Envia uma imagem e devolve as URLs. Não define Content-Type de propósito: o
 * navegador precisa gerar o boundary do multipart sozinho, e informá-lo à mão
 * quebra o envio de um jeito difícil de diagnosticar.
 */
export async function enviarImagem(arquivo: File): Promise<ArquivoSalvo> {
  const dados = new FormData();
  dados.append('arquivo', arquivo);
  return api<ArquivoSalvo>('/api/arquivos', { method: 'POST', body: dados });
}
